import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { defaultVerbaPagamentoFormValues, VerbaPagamentoForm, VerbaPagamentoSchema } from "../../../schemas/VerbaPagamento_lista/verba_pagamento";
import { VerbaPagamento } from "../../../../domain/models/verba_pagamento";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";

import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";

/**
 * CadastroVerbaPagamentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  selectedVerbaPagamento: VerbaPagamento | null;
  onSubmit: (form: VerbaPagamentoForm) => void;
  onOpenChangeHistory: () => void;
  onClose: () => void;
};

export const CadastroVerbaPagamentoModal = ({
  open,
  saving,
  selectedVerbaPagamento,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<VerbaPagamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultVerbaPagamentoFormValues,
    resolver: zodResolver(VerbaPagamentoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedVerbaPagamento) {
        const { ativo, nrItemVerba, tipo, valorItemVerba, descricao } = selectedVerbaPagamento;
        hookForm.setValue('ativo', ativo ?? ativo ?? false);
        hookForm.setValue('nro', nrItemVerba);
        hookForm.setValue('tipo', tipo ?? 0);
        hookForm.setValue('valor', valorItemVerba !== null ? valorItemVerba : null);
        hookForm.setValue('verba', descricao ?? "");
      } else {
        hookForm.reset({
          ativo: defaultVerbaPagamentoFormValues.ativo,
          tipo: defaultVerbaPagamentoFormValues.tipo,
          valor: null,
        })
      }
    }
  }, [
    hookForm,
    open,
    selectedVerbaPagamento,
  ]);

  const handleSubmit = useCallback((form: VerbaPagamentoForm) => {
    if (!saving) onSubmit(form);
  }, [saving, onSubmit]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>

      <DialogTitle>
        Cadastrar de Verba
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <ModalContainer>
            <FormContainer title={"Informações"} arrangement="fullRow" border={false}>
              <>
                <FormSwitchInput
                  name='ativo'
                  readOnly={saving}
                  reactHookForm={hookForm} />

                <FormRadioGroupInput
                  name='tipo'
                  options={[
                    {
                      id: 0,
                      label: 'Acréscimo (+)',
                      style: {
                        backgroundColor: '#45b6af',
                        color: 'white',
                        padding: '.2rem .4rem',
                        fontSize: '0.85rem',
                      },
                    },
                    {
                      id: 1,
                      label: 'Desconto (-)',
                      style: {
                        backgroundColor: '#f3565d',
                        color: 'white',
                        padding: '.2rem .4rem',
                        fontSize: '0.85rem',
                      },
                    },
                  ]}
                  reactHookForm={hookForm} />

                <DefaultFormInput
                  name={"nro"}
                  label="Nº"
                  reactHookForm={hookForm} />

                <DefaultFormInput
                  name={"verba"}
                  label="Verba"
                  required={true}
                  reactHookForm={hookForm} />

                <DefaultFormMoneyInput
                  name={"valor"}
                  label={"Valor (R$)"}
                  reactHookForm={hookForm}
                />

              </>
            </FormContainer>
          </ModalContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedVerbaPagamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={onOpenChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>

            }
          </div>

          <div className="flex flex-row">
            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={handleClose}>
                  Cancelar
                </h1>
              </div>

              <div className="w-[120px]">
                {saving ? (
                  <PrimaryButton
                    buttonColor="primary-green">
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    buttonColor="primary-green">
                    Salvar
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}